import { GetTask } from '@/Core/Tasks/Utils/GetTask'
import { SetTask } from '@/Core/Tasks/Utils/SetTask'
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx'
import { UIManager } from '../../../Managers/UI/UIManager'
import { BaseFieldStore } from './BaseFieldStore'
import { ComputedFieldManager } from './ComputedFieldManager'
import { FieldStoreFactory } from './FieldStoreFactory'
import { FieldType, IFieldDefinition } from './Types/FieldTypeEnum'

/**
 * Основной класс для управления формами
 */
export class FormStore extends UIManager {
    name: string | null = null
    fields: Map<string, BaseFieldStore> = new Map()
    values: Record<string, any> = {}
    initialValues: Record<string, any> = {}
    errors: Record<string, string> = {}

    isEditable = true
    isLoading = false

    // Шаблони форми (з старого ядра)
    override template: any = null
    templateView: any = null
    templateMultiple: any = null
    templateObject: string = 'vertical'

    // Зберігаємо оригінальну конфігурацію полів
    fieldsSource: Record<string, IFieldDefinition> = {}

    // Конфігурація форми (fieldTypes, fieldTemplates, defaultFieldTemplate)
    config: any = null

    // Поточна мова для мультимовних полів
    currentLanguage: string = 'uk_UA'

    // Менеджер обчислювальних полів
    private computedFieldManager: ComputedFieldManager

    constructor({
        name,
        fields,
        initialValues,
        isEditable,
        template,
        templateView,
        templateMultiple,
        templateObject,
        currentLanguage,
        ...props
    }: {
        name?: string
        fields?: Record<string, IFieldDefinition>
        initialValues?: Record<string, any>
        isEditable?: boolean
        template?: any
        templateView?: any
        templateMultiple?: any
        templateObject?: string
        currentLanguage?: string
        [key: string]: any
    }) {
        super(props)

        // Ініціалізуємо менеджер обчислювальних полів
        this.computedFieldManager = new ComputedFieldManager(this)

        makeObservable(this, {
            name: observable,
            fields: observable,
            values: observable,
            initialValues: observable,
            errors: observable,
            isEditable: observable,
            isLoading: observable,
            template: observable,
            templateView: observable,
            templateMultiple: observable,
            templateObject: observable,
            fieldsSource: observable,
            config: observable,
            currentLanguage: observable,
            isValid: computed,
            isTranslatable: computed,
            setName: action,
            setValue: action,
            setInitialValue: action,
            setInitialValues: action,
            setError: action,
            setErrors: action,
            resetErrors: action,
            setIsEditable: action,
            setIsLoading: action,
            setCurrentLanguage: action,
            validate: action,
            reset: action,
            clear: action,
        })

        // Зберігаємо конфігурацію форми з props
        // Конфігурація містить fieldTypes, fieldTemplates, defaultFieldTemplate
        if (props.fieldTypes || props.fieldTemplates || props.defaultFieldTemplate) {
            this.config = {
                fieldTypes: props.fieldTypes,
                fieldTemplates: props.fieldTemplates,
                defaultFieldTemplate: props.defaultFieldTemplate,
            }
        }

        // Встановлюємо шаблони
        if (template) this.template = template
        if (templateView) this.templateView = templateView
        if (templateMultiple) this.templateMultiple = templateMultiple
        if (templateObject) this.templateObject = templateObject
        if (currentLanguage) this.currentLanguage = currentLanguage

        if (name) {
            this.name = name
        }

        if (fields) {
            this.fieldsSource = fields
            this.createFields(fields)
        }

        if (initialValues) {
            this.setInitialValues(initialValues)
        }

        if (isEditable !== undefined) {
            this.setIsEditable(isEditable)
        }
    }

    // Computed properties
    get isValid(): boolean {
        return Object.keys(this.errors).length === 0
    }

    /**
     * Перевіряє чи форма має мультимовні поля
     */
    get isTranslatable(): boolean {
        return Object.values(this.fieldsSource).some((field: any) => field.isTranslatable)
    }

    /**
     * Встановлює поточну мову
     */
    setCurrentLanguage(language: string): void {
        this.currentLanguage = language
    }

    // Field management
    createFields(fieldsConfig: Record<string, IFieldDefinition>): void {
        // Спочатку створюємо всі поля
        Object.entries(fieldsConfig).forEach(([fieldName, fieldDef]) => {
            const fieldDefinition: IFieldDefinition = {
                ...fieldDef,
                name: fieldName,
                strategy: fieldDef.strategy || FieldStoreFactory.inferStrategy(fieldDef),
            }

            const field = FieldStoreFactory.createField(fieldDefinition, this)
            this.fields.set(fieldName, field)

            // Реєструємо обчислювальні поля
            if (fieldDef.type === FieldType.COMPUTED && fieldDef.computeFn && fieldDef.dependencies) {
                this.registerComputedField([fieldName], fieldDef.computeFn, fieldDef.dependencies)
            }

            // Зберігаємо визначення обчислювальних полів для TABLE
            if (fieldDef.type === FieldType.TABLE && fieldDef.fields) {
                this.registerTableComputedFields(fieldName, fieldDef.fields)
            }
        })

        // Після створення всіх полів реєструємо обчислювальні поля для існуючих рядків TABLE
        Object.entries(fieldsConfig).forEach(([fieldName, fieldDef]) => {
            if (fieldDef.type === FieldType.TABLE) {
                const tableField = this.fields.get(fieldName)
                if (tableField && 'rows' in tableField) {
                    const rowCount = (tableField as any).rows.length
                    for (let i = 0; i < rowCount; i++) {
                        this.registerTableRowComputedFields(fieldName, i)
                    }
                }
            }
        })
    }

    /**
     * Реєструє обчислювальні поля всередині TABLE
     */
    private registerTableComputedFields(tableName: string, fields: Record<string, IFieldDefinition>): void {
        Object.entries(fields).forEach(([fieldName, fieldDef]) => {
            if (fieldDef.type === FieldType.COMPUTED && fieldDef.computeFn && fieldDef.dependencies) {
                // Для TABLE полів треба реєструвати для кожного рядка динамічно
                // Це буде виконуватись при додаванні рядків в TableFieldStore
                // Зберігаємо визначення для подальшого використання
                if (!this.tableComputedFieldsDefinitions) {
                    this.tableComputedFieldsDefinitions = new Map()
                }

                if (!this.tableComputedFieldsDefinitions.has(tableName)) {
                    this.tableComputedFieldsDefinitions.set(tableName, new Map())
                }

                this.tableComputedFieldsDefinitions.get(tableName)!.set(fieldName, {
                    computeFn: fieldDef.computeFn,
                    dependencies: fieldDef.dependencies,
                })
            }
        })
    }

    /**
     * Реєструє обчислювальне поле для конкретного рядка TABLE
     */
    registerTableRowComputedField(tableName: string, rowIndex: number, fieldName: string): void {
        const tableFields = this.tableComputedFieldsDefinitions?.get(tableName)
        if (!tableFields) return

        const fieldDef = tableFields.get(fieldName)
        if (!fieldDef) return

        const path = [tableName, rowIndex.toString(), fieldName]
        this.registerComputedField(path, fieldDef.computeFn, fieldDef.dependencies)
    }

    /**
     * Реєструє всі обчислювальні поля для рядка TABLE
     */
    registerTableRowComputedFields(tableName: string, rowIndex: number): void {
        const tableFields = this.tableComputedFieldsDefinitions?.get(tableName)
        if (!tableFields) return

        tableFields.forEach((fieldDef, fieldName) => {
            const path = [tableName, rowIndex.toString(), fieldName]
            this.registerComputedField(path, fieldDef.computeFn, fieldDef.dependencies)
        })
    }

    /**
     * Реєструє обчислювальне поле
     */
    private registerComputedField(path: string[], computeFn: any, dependencies: string[]): void {
        this.computedFieldManager.registerComputedField(path, computeFn, dependencies)
    }

    /**
     * Видаляє обчислювальне поле рядка TABLE
     */
    unregisterTableRowComputedFields(tableName: string, rowIndex: number): void {
        const tableFields = this.tableComputedFieldsDefinitions?.get(tableName)
        if (!tableFields) return

        tableFields.forEach((_, fieldName) => {
            const path = [tableName, rowIndex.toString(), fieldName]
            this.computedFieldManager.unregisterComputedField(path)
        })
    }

    // Зберігаємо визначення обчислювальних полів для TABLE
    private tableComputedFieldsDefinitions?: Map<
        string,
        Map<
            string,
            {
                computeFn: any
                dependencies: string[]
            }
        >
    >

    getField(path: string | string[]): BaseFieldStore | undefined {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        return this.fields.get(pathStr)
    }

    setField(path: string | string[], field: BaseFieldStore): void {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        this.fields.set(pathStr, field)
    }

    deleteField(path: string | string[]): void {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        this.fields.delete(pathStr)
    }

    // Value management
    setValue(path: string | string[], value: any): void {
        runInAction(() => {
            SetTask(this.values, path, value)
        })

        // Debounce для текстовых полів
        const field = this.getField(path)
        if (field && this.shouldDebounce(field.type)) {
            this.debounceOnChange(path, value)
        } else {
            this.triggerOnChange(path, value)
        }
    }

    getValue(path: string | string[], defaultValue?: any): any {
        return GetTask(this.values, path) ?? defaultValue
    }

    setInitialValue(path: string | string[], value: any): void {
        runInAction(() => {
            SetTask(this.initialValues, path, value)
            SetTask(this.values, path, value)
        })
    }

    getInitialValue(path: string | string[]): any {
        return GetTask(this.initialValues, path)
    }

    setInitialValues(values: Record<string, any>): void {
        this.initialValues = { ...values }
        this.values = { ...values }

        // Sync rows for any TABLE fields — rows were initialized before API data arrived.
        // syncRowsWithValues only adds missing stores; never rebuilds existing ones.
        this.fields.forEach((field) => {
            if (typeof (field as any).syncRowsWithValues === 'function') {
                ;(field as any).syncRowsWithValues()
            }
        })
    }

    // Error management
    setError(path: string | string[], error: string | null): void {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        if (error === null) {
            delete this.errors[pathStr]
        } else {
            this.errors[pathStr] = error
        }
    }

    getError(path: string | string[]): string | null {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        return this.errors[pathStr] || null
    }

    setErrors(errors: Record<string, string>): void {
        this.errors = { ...errors }
    }

    resetErrors(): void {
        this.errors = {}
    }

    // State management
    setName(name: string): void {
        this.name = name
    }

    setIsEditable(isEditable: boolean): void {
        this.isEditable = isEditable
    }

    setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading
    }

    // Form operations
    validate(): boolean {
        this.resetErrors()

        let isValid = true
        this.fields.forEach((field) => {
            if (!field.validate()) {
                isValid = false
            }
        })

        return isValid
    }

    async submit(formComposer?: any): Promise<any> {
        this.validate()
        console.log({ _: this});
        
        if (!this.isValid) {
            return
        }

        const eventManager = this.getEventManager()

        await eventManager.emitAsync('onBeforeSubmit')

        const values = toJS(this.values)
        const initialValues = toJS(this.initialValues)
        
        const result = await eventManager.emitAsync('onSubmit', {
            values,
            initialValues,
            formComposer,
            formContext: this,
        })

        await eventManager.emitAsync('onAfterSubmit')

        return result
    }

    reset(): void {
        this.values = { ...this.initialValues }
        this.resetErrors()
    }

    override clear(): void {
        this.values = {}
        this.initialValues = {}
        this.resetErrors()
    }

    /**
     * Очищення ресурсів при знищенні форми
     */
    dispose(): void {
        // Очищуємо всі debounce таймери
        this.debounceTimeouts.forEach((timeout) => clearTimeout(timeout))
        this.debounceTimeouts.clear()

        // Очищуємо всі обчислювальні поля
        this.computedFieldManager.dispose()
    }

    // Helper methods
    private shouldDebounce(fieldType: FieldType): boolean {
        return fieldType === FieldType.STRING || fieldType === FieldType.TEXTAREA || fieldType === FieldType.NUMBER
    }

    private debounceTimeouts: Map<string, any> = new Map()

    private debounceOnChange(path: string | string[], value: any): void {
        const pathStr = Array.isArray(path) ? path.join('.') : path

        // Очищаем предыдущий таймер
        const existingTimeout = this.debounceTimeouts.get(pathStr)
        if (existingTimeout) {
            clearTimeout(existingTimeout)
        }

        // Устанавливаем новый таймер
        const timeoutId = setTimeout(() => {
            this.triggerOnChange(path, value)
            this.debounceTimeouts.delete(pathStr)
        }, 700)

        this.debounceTimeouts.set(pathStr, timeoutId)
    }

    private triggerOnChange(path: string | string[], value: any): void {
        const pathStr = Array.isArray(path) ? path.join('.') : path
        const changedValues = { [pathStr]: value }
        
        // Emit onChange event
        this.getEventManager().emit('onChange', {
            changedValues,
            values: toJS(this.values),
            formContext: this,
        })
    }
}
